import React from 'react';
import MetaData from '../restaurant-details/components/meta-data';
import AdUnit from '../../components/ad-unit';
import Link from './Link';
import { TemplatedLazyImage } from '../../components/image';
import placeholderSrc from '../restaurant-details/components/menu/placeholder.svg';
import {
    Breadcrumbs,
    ContactCard,
    DeliverySection,
    DirectionsButton,
    HoursCard,
    OpenStatusBadge,
    useRestaurantHours,
    LocationSection,
    PageFrame,
    PinIcon,
    Section,
    TwoColumns,
    correctionTriggerClass,
    type DeliveryProvider,
} from './parts';
import type { Address, OpenHoursRow } from './format';
import { idFromPath } from '../../components/report-correction/correction';
import { usePageView } from './views';

/**
 * The restaurant page rebuilt in the new framework (W3.1), used in place of
 * restaurant-details/View when ENABLE_NEW_DETAIL_PAGES is on at build time.
 * Same query fragments, head tags (the original MetaData), status codes and
 * owner/correction gating as the original; see docs/parity/w3-1-detail-pages.md.
 */

// Required inside DefinePlugin-constant branches, as on the original page, so a
// build with the flag off carries neither entry nor its dialogs.
const RestaurantCorrectionLink:
    | typeof import('../../components/report-correction/ReportCorrectionLink').RestaurantCorrectionLink
    | null =
    process.env.ENABLE_CORRECTIONS && process.env.ENABLE_ACCOUNTS
        ? // eslint-disable-next-line @typescript-eslint/no-require-imports
          require('../../components/report-correction/ReportCorrectionLink')
              .RestaurantCorrectionLink
        : null;

// Lists (prototype SaveToListModal): only with ENABLE_LISTS and ENABLE_ACCOUNTS.
const SaveButton:
    typeof import('../../components/lists/SaveButton').SaveButton | null =
    process.env.ENABLE_LISTS && process.env.ENABLE_ACCOUNTS
        ? // eslint-disable-next-line @typescript-eslint/no-require-imports
          require('../../components/lists/SaveButton').SaveButton
        : null;

// Restaurant follows (prototype RestaurantDetail Follow): with ENABLE_FOLLOWS and ENABLE_ACCOUNTS.
const RestaurantFollow:
    | typeof import('../../components/follows/RestaurantFollow').RestaurantFollow
    | null =
    process.env.ENABLE_FOLLOWS && process.env.ENABLE_ACCOUNTS
        ? // eslint-disable-next-line @typescript-eslint/no-require-imports
          require('../../components/follows/RestaurantFollow').RestaurantFollow
        : null;

// Restaurant posts and stories ("Updates from"): only with ENABLE_FEED. Loads in the browser.
const RestaurantUpdates:
    | typeof import('../../components/feed/RestaurantUpdates').RestaurantUpdates
    | null = process.env.ENABLE_FEED
    ? // eslint-disable-next-line @typescript-eslint/no-require-imports
      require('../../components/feed/RestaurantUpdates').RestaurantUpdates
    : null;

const ClaimCard: typeof import('./ClaimEntries').ClaimCard | null = process.env
    .ENABLE_FOR_RESTAURANTS
    ? // eslint-disable-next-line @typescript-eslint/no-require-imports
      require('./ClaimEntries').ClaimCard
    : null;

export interface MenuMeal {
    id?: string | null;
    name: string;
    path: string;
    description?: string | null;
    price?: { display?: string | null } | null;
    mainImage?: { templatedUrl?: string | null } | null;
    /** W2.6: the meal's section; it groups the menu when it names one of menuSections. */
    menuSection?: string | null;
}

export interface RestaurantPageData {
    restaurant: {
        name: string;
        path: string;
        website?: string | null;
        phone?: string | null;
        address?: Address | null;
        openHours?: OpenHoursRow[] | null;
        delivery?: DeliveryProvider[] | null;
        meals?: MenuMeal[] | null;
        /** W2.6: the owner's menu categories in order ([] when none). */
        menuSections?: string[] | null;
        /** W2.6: the owner's description and photos (first = main). */
        description?: string | null;
        images?: RestaurantPhoto[] | null;
    };
}

export interface RestaurantPhoto {
    id?: string | null;
    templatedUrl?: string | null;
}

/** Photos shown in the strip; the rest wait for a gallery. */
const STRIP_PHOTOS = 5;

/**
 * The owner's photos, first and largest (photos first: diners decide by looking). One photo
 * fills the strip; more sit beside it in a 2x2 grid on wider screens and in a row below on
 * phones. Nothing renders until an owner has added photos.
 */
const PhotoStrip = ({
    name,
    photos,
}: {
    name: string;
    photos: RestaurantPhoto[] | null | undefined;
}) => {
    const shown = (photos ?? [])
        .filter(p => p.templatedUrl)
        .slice(0, STRIP_PHOTOS);
    if (shown.length === 0) return null;
    const [main, ...rest] = shown;
    return (
        <div
            className={
                rest.length
                    ? 'mb-6 grid grid-cols-4 gap-2 md:mb-8 md:grid-rows-2'
                    : 'mb-6 md:mb-8'
            }
        >
            <div
                className={
                    rest.length
                        ? 'col-span-4 aspect-[3/2] overflow-hidden rounded-lg bg-surface-subtle md:col-span-2 md:row-span-2 md:aspect-auto'
                        : 'aspect-[3/2] overflow-hidden rounded-lg bg-surface-subtle md:aspect-[21/9]'
                }
            >
                <TemplatedLazyImage
                    templatedUrl={main.templatedUrl}
                    lazy={false}
                    size="1200x800"
                    options="fit=cover"
                    alt={`${name}, photo 1`}
                    className="block size-full object-cover"
                />
            </div>
            {rest.map((photo, i) => (
                <div
                    key={photo.id ?? i}
                    className="aspect-square overflow-hidden rounded-lg bg-surface-subtle md:aspect-[4/3]"
                >
                    <TemplatedLazyImage
                        templatedUrl={photo.templatedUrl}
                        lazy={true}
                        placeholder={placeholderSrc}
                        size="600x450"
                        options="fit=cover"
                        alt={`${name}, photo ${i + 2}`}
                        className="block size-full object-cover"
                    />
                </div>
            ))}
        </div>
    );
};

/** `Title` is h4 under a category heading, h3 when the menu is one list. */
const MenuCard = ({
    meal,
    Title = 'h3',
}: {
    meal: MenuMeal;
    Title?: 'h3' | 'h4';
}) => (
    <li className="relative flex gap-4 rounded-lg border border-border-subtle bg-surface p-4 transition-shadow focus-within:ring-2 focus-within:ring-ring hover:shadow-card">
        <div className="flex min-w-0 flex-1 flex-col">
            <Title className="text-subtitle-1 font-semibold break-words text-ink-strong">
                {/* The original menu links with rel nofollow; kept for crawl parity. */}
                <Link
                    to={meal.path}
                    rel="noopener noreferrer nofollow"
                    className="text-ink-strong no-underline outline-none after:absolute after:inset-0 after:rounded-lg after:content-['']"
                >
                    {meal.name}
                </Link>
            </Title>
            {meal.description && (
                <p className="mt-1 line-clamp-2 text-body-2 text-ink-muted">
                    {meal.description}
                </p>
            )}
            {meal.price?.display && (
                <p className="mt-auto pt-3 text-subtitle-1 font-semibold text-ink">
                    {meal.price.display}
                </p>
            )}
        </div>
        <div className="size-24 shrink-0 overflow-hidden rounded-md bg-surface-subtle md:size-28">
            <TemplatedLazyImage
                templatedUrl={meal.mainImage?.templatedUrl || placeholderSrc}
                lazy={true}
                placeholder={placeholderSrc}
                size="250x250"
                options="fit=cover"
                alt={meal.name}
                title={meal.name}
                className="block size-full object-cover"
            />
        </div>
    </li>
);

interface MenuGroup {
    /** null: the whole menu, ungrouped. */
    title: string | null;
    meals: MenuMeal[];
}

/**
 * As the prototype's restaurant page: with owner categories, one group per category in
 * order (empty ones left out), then "Other" for the rest; without, one list.
 */
export function groupMenu(
    meals: MenuMeal[],
    sections: string[] | null | undefined,
): MenuGroup[] {
    if (!sections?.length) return [{ title: null, meals }];
    const byKey = new Map(sections.map(name => [name.toLowerCase(), name]));
    const sectionOf = (m: MenuMeal) =>
        (m.menuSection && byKey.get(m.menuSection.toLowerCase())) || null;
    const groups: MenuGroup[] = sections.map(title => ({
        title,
        meals: meals.filter(m => sectionOf(m) === title),
    }));
    groups.push({ title: 'Other', meals: meals.filter(m => !sectionOf(m)) });
    return groups.filter(g => g.meals.length > 0);
}

const MenuSection = ({
    restaurantName,
    meals,
    sections,
}: {
    restaurantName: string;
    meals: MenuMeal[] | null | undefined;
    sections?: string[] | null;
}) => (
    <Section
        id="menu-heading"
        title="Menu"
        className="mt-10 border-t border-border-subtle pt-8 md:mt-12"
        action={
            meals && meals.length > 0 ? (
                <span className="text-body-2 text-ink-muted">
                    {meals.length} {meals.length === 1 ? 'dish' : 'dishes'}
                </span>
            ) : undefined
        }
    >
        {!meals ? (
            <p className="text-body-2 text-ink-muted">Currently no menu</p>
        ) : (
            <>
                <p className="mb-4 text-body-2 text-ink-muted">
                    Dishes at {restaurantName}. Prices may vary in venue.
                </p>
                {groupMenu(meals, sections).map(group => (
                    <div key={group.title ?? 'all'} className="mb-8 last:mb-0">
                        {group.title !== null && (
                            <h3 className="mb-3 text-title-3 font-semibold text-ink-strong">
                                {group.title}
                            </h3>
                        )}
                        <ul className="grid grid-cols-1 gap-4 md:grid-cols-[repeat(auto-fill,minmax(20rem,1fr))]">
                            {group.meals.map((meal, index) => (
                                <MenuCard
                                    key={meal.id ?? index}
                                    meal={meal}
                                    Title={group.title === null ? 'h3' : 'h4'}
                                />
                            ))}
                        </ul>
                    </div>
                ))}
            </>
        )}
    </Section>
);

const RestaurantPage = ({ data }: { data: RestaurantPageData }) => {
    const { restaurant } = data;
    const hours = useRestaurantHours(idFromPath(restaurant.path));
    usePageView(idFromPath(restaurant.path));
    return (
        <>
            <MetaData data={data} />
            <PageFrame>
                <Breadcrumbs
                    items={[
                        { label: 'Home', to: '/' },
                        { label: restaurant.name },
                    ]}
                />
                <PhotoStrip name={restaurant.name} photos={restaurant.images} />
                <TwoColumns
                    main={
                        <>
                            <header>
                                <h1 className="text-title-1 font-semibold break-words text-ink-strong md:text-large-title">
                                    {restaurant.name}
                                </h1>
                                {restaurant.address?.display && (
                                    <p className="mt-2 flex items-start gap-2 text-body-1 text-ink-muted">
                                        <PinIcon className="mt-0.5" />
                                        <span>
                                            {restaurant.address.display}
                                        </span>
                                    </p>
                                )}
                                <OpenStatusBadge
                                    hours={hours}
                                    className="mt-3"
                                />
                                {(SaveButton || RestaurantFollow) && (
                                    <div className="mt-4 flex flex-wrap items-start gap-3">
                                        {SaveButton && (
                                            <SaveButton
                                                legacyRestaurantId={idFromPath(
                                                    restaurant.path,
                                                )}
                                                itemName={restaurant.name}
                                            />
                                        )}
                                        {RestaurantFollow &&
                                            idFromPath(restaurant.path) && (
                                                <RestaurantFollow
                                                    legacyRestaurantId={idFromPath(
                                                        restaurant.path,
                                                    )!}
                                                />
                                            )}
                                    </div>
                                )}
                            </header>
                            {restaurant.description?.trim() && (
                                <Section
                                    id="about-heading"
                                    title="About"
                                    className="mt-6"
                                >
                                    <p className="text-body-1 whitespace-pre-line text-ink">
                                        {restaurant.description.trim()}
                                    </p>
                                </Section>
                            )}
                            <LocationSection
                                name={restaurant.name}
                                address={restaurant.address}
                            />
                            <DeliverySection delivery={restaurant.delivery} />
                            {RestaurantUpdates &&
                                idFromPath(restaurant.path) && (
                                    <RestaurantUpdates
                                        legacyRestaurantId={idFromPath(
                                            restaurant.path,
                                        )!}
                                        restaurantName={restaurant.name}
                                    />
                                )}
                            <AdUnit
                                slot="restaurant-details-middle"
                                format="rectangle"
                            />
                        </>
                    }
                    aside={
                        <>
                            <AdUnit
                                slot="restaurant-details-sidebar"
                                format="rectangle"
                                sticky={true}
                            />
                            <ContactCard
                                phone={restaurant.phone}
                                website={restaurant.website}
                            />
                            {ClaimCard && (
                                <ClaimCard
                                    restaurantName={restaurant.name}
                                    restaurantPath={restaurant.path}
                                />
                            )}
                            <HoursCard
                                openHours={restaurant.openHours}
                                hours={hours}
                            />
                            <DirectionsButton address={restaurant.address} />
                            {RestaurantCorrectionLink && (
                                <RestaurantCorrectionLink
                                    restaurant={restaurant}
                                    appearance="button"
                                    triggerClassName={correctionTriggerClass}
                                />
                            )}
                        </>
                    }
                />
                <MenuSection
                    restaurantName={restaurant.name}
                    meals={restaurant.meals}
                    sections={restaurant.menuSections}
                />
                <AdUnit slot="restaurant-details-footer" format="horizontal" />
            </PageFrame>
        </>
    );
};

export default RestaurantPage;
